/**
 * Parsed filter token used by `makeMatcher` to evaluate whether a string
 * matches a user's filter input.
 */
export interface FilterToken {
  /** The pattern to match. */
  pattern: string;

  /**
   * When true, this token must NOT appear in the text. Currently only
   * possible in regex mode via `isWholePatternInvert`; the field is kept on
   * the token shape for forward-compatibility with future include/exclude
   * syntax that doesn't conflict with characters that can appear in names.
   */
  negate: boolean;
}

export interface ParsedFilter {
  tokens: FilterToken[];

  /**
   * Regex mode only. When true, the matcher should invert the result of the
   * regex test (i.e., `!` prefix). Always false in literal mode.
   */
  isWholePatternInvert: boolean;
}

/**
 * Parses a filter input string into tokens.
 *
 * - Literal mode (`isRegex=false`): the entire trimmed input is treated as
 *   one literal substring. Spaces, dashes, and other punctuation are
 *   matched literally so the user can search for names that contain those
 *   characters (e.g. `_armed` or `image data`).
 * - Regex mode (`isRegex=true`): the entire trimmed input is one regex
 *   pattern. A leading `!` marks the result as inverted; `\!` escapes a
 *   literal leading `!`.
 */
export function parseFilterTerm(term: string, isRegex: boolean): ParsedFilter {
  const trimmed = term.trim();

  if (trimmed === "") {
    return { tokens: [], isWholePatternInvert: false };
  }

  if (isRegex) {
    return parseRegexFilter(trimmed);
  }

  return {
    tokens: [{ pattern: trimmed, negate: false }],
    isWholePatternInvert: false,
  };
}

function parseRegexFilter(trimmed: string): ParsedFilter {
  if (trimmed.startsWith("\\!")) {
    // Escaped leading "!": positive token starting with literal "!"
    const pattern = trimmed.slice(1);
    return {
      tokens: [{ pattern, negate: false }],
      isWholePatternInvert: false,
    };
  }

  if (trimmed.startsWith("!")) {
    const pattern = trimmed.slice(1);
    if (pattern === "") {
      // Lone "!" with nothing else: treat as no tokens.
      return { tokens: [], isWholePatternInvert: false };
    }
    return {
      tokens: [{ pattern, negate: false }],
      isWholePatternInvert: true,
    };
  }

  return {
    tokens: [{ pattern: trimmed, negate: false }],
    isWholePatternInvert: false,
  };
}
